import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Box,
} from '@mui/material';
import { identityApi } from '../api/identityApi'; // Import identityApi client
import { logError } from '../utils/logging'; // Import logError

function Register() {

  const [email, setEmail] = useState('');
  const [apiKey, setApiKey] = useState(''); // This seems to be unused in the current code
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      await identityApi.register(email);
      setSuccess('If the email is eligible, a verification link has been sent.');
    } catch (err) {
      logError(err, { context: 'Register handleSubmit' });
      // Handle Pydantic validation errors properly
      let errorMessage = 'Registration failed. Please try again.';
      
      if (err.message) { // use err.message from API client
        try {
          const errorDetail = JSON.parse(err.message);
          if (Array.isArray(errorDetail.detail)) {
            // Pydantic validation error - extract first error message
            const firstError = errorDetail.detail[0];
            if (firstError && firstError.msg) {
              errorMessage = firstError.msg;
            }
          } else if (typeof errorDetail.detail === 'string') {
            // Simple string error
            errorMessage = errorDetail.detail;
          } else {
            errorMessage = err.message;
          }
        } catch (parseError) {
          errorMessage = err.message;
        }
      }
      
      setError(errorMessage);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        position: 'relative',
        background: 'linear-gradient(180deg, #0a0a0a 0%, #1a1a1a 50%, #0a0a0a 100%)',
      }}
    >
      {/* Background gradient overlay */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 50% 0%, rgba(25, 118, 210, 0.15) 0%, transparent 50%)',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          width: '100vw',
          left: '50%',
          right: '50%',
          marginLeft: '-50vw',
          marginRight: '-50vw',
          py: 8,
          zIndex: 1,
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: '100%',
            px: { xs: 4, sm: 5, md: 6 },
            mx: 'auto',
            position: 'relative',
            zIndex: 1,
          }}
        >
          <Box
            sx={{
              maxWidth: '500px',
              mx: 'auto',
            }}
          >
            <Paper 
              elevation={0} 
              sx={{ 
                p: 4, 
                bgcolor: 'rgba(26, 26, 26, 0.8)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 2,
              }}
            >
              <Typography 
                variant="h4" 
                component="h1" 
                gutterBottom
                align="center"
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.95)',
                  fontWeight: 600,
                  mb: 3
                }}
              >
                Register for STT Service
              </Typography>
              
              {error && (
                <Alert severity="error" sx={{ mb: 3 }} data-test-id="register-error">
                  {error}
                </Alert>
              )}

              {success && (
                <Alert severity="success" sx={{ mb: 3 }} data-test-id="register-success">
                  {success}
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmit} data-test-id="register-form">
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  inputProps={{ 'data-test-id': 'register-email' }}
                  sx={{
                    mb: 2.5,
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'rgba(255, 255, 255, 0.05)',
                      '& fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.2)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.3)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#1976d2',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: 'rgba(255, 255, 255, 0.7)',
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#1976d2',
                    },
                    '& .MuiOutlinedInput-input': {
                      color: 'rgba(255, 255, 255, 0.95)',
                    },
                  }}
                />
                
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  size="large"
                  data-test-id="register-submit"
                  sx={{
                    mt: 1,
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 600,
                    textTransform: 'none',
                  }}
                >
                  Register
                </Button>

                <Box sx={{ mt: 3, textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Already have an account?{' '}
                    <Link to="/login" style={{ textDecoration: 'none' }} data-test-id="register-login-link">
                      <Typography 
                        component="span" 
                        variant="body2" 
                        sx={{ 
                          color: '#1976d2',
                          fontWeight: 500,
                          '&:hover': {
                            textDecoration: 'underline'
                          }
                        }}
                      >
                        Login here
                      </Typography>
                    </Link>
                  </Typography>
                </Box>
              </Box>

              {apiKey && (
                <Box sx={{ mt: 3, p: 2, borderRadius: 1, bgcolor: 'rgba(255, 255, 255, 0.05)' }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ color: 'rgba(255, 255, 255, 0.95)' }}>
                    Your API Key:
                  </Typography>
                  <Typography
                    component="pre"
                    sx={{
                      p: 1,
                      borderRadius: 1,
                      overflow: 'auto',
                      bgcolor: 'rgba(0, 0, 0, 0.3)',
                      color: 'rgba(255, 255, 255, 0.9)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                    }}
                  >
                    {apiKey}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, color: 'rgba(255, 255, 255, 0.7)' }}>
                    Please save this API key. You'll need it to use the STT service.
                  </Typography>
                </Box>
              )}
            </Paper>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default Register;